import { defaultBranding, type BrandingTokens } from "./tokens";

type Rgb = [number, number, number];
const white: Rgb = [1, 1, 1];
const black: Rgb = [0, 0, 0];

function rgb(value: string): Rgb | null {
  const parts = value.match(/^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/);
  if (!parts) return null;
  const h = Number(parts[1]),
    s = Number(parts[2]) / 100,
    l = Number(parts[3]) / 100;
  if (h > 360 || s > 1 || l > 1) return null;
  const a = s * Math.min(l, 1 - l);
  const channel = (n: number) => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return [channel(0), channel(8), channel(4)];
}

function blend(foreground: Rgb, background: Rgb, alpha: number): Rgb {
  return foreground.map((value, i) => value * alpha + background[i]! * (1 - alpha)) as Rgb;
}

function contrast(first: Rgb, second: Rgb) {
  const luminance = (color: Rgb) =>
    color
      .map((c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4))
      .reduce((sum, c, i) => sum + c * [0.2126, 0.7152, 0.0722][i]!, 0);
  const a = luminance(first),
    b = luminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

const colors = [
  "brand",
  "brandForeground",
  "brandHover",
  "brandMuted",
  "accent",
  "accentForeground",
] as const;

/** Vérifie les couples de couleurs utilisés par les boutons, liens, badges et en-têtes. */
export function hasAccessibleBrandingColors(tokens: BrandingTokens): boolean {
  const parsed = Object.fromEntries(colors.map((key) => [key, rgb(tokens[key])])) as Record<
    (typeof colors)[number],
    Rgb | null
  >;
  if (colors.some((key) => !parsed[key])) return false;
  const { brand, brandForeground, brandHover, brandMuted, accent, accentForeground } =
    parsed as Record<(typeof colors)[number], Rgb>;
  // La classe text-brand mélange le survol et 8 % de noir.
  const brandText = blend(black, brandHover, 0.08);
  // Borne conservatrice des trois couches décoratives de PageHero.
  const heroBackground = blend(brand, blend(white, blend(brand, accent, 0.096), 0.072), 0.2);
  const pairs: [Rgb, Rgb][] = [
    [brandForeground, brand],
    [brandForeground, brandHover],
    [brandText, white],
    [brandText, brandMuted],
    [brandText, blend(brand, white, 0.2)],
    [accent, white],
    [accent, blend(accent, white, 0.2)],
    [accentForeground, accent],
    [blend(accentForeground, heroBackground, 0.75), heroBackground],
    [accentForeground, blend(white, heroBackground, 0.1)],
  ];
  return pairs.every(([foreground, background]) => contrast(foreground, background) >= 4.5);
}

/** Les anciennes personnalisations illisibles utilisent la palette Aon, sans modifier les données enregistrées. */
export function accessibleBranding(tokens: BrandingTokens): BrandingTokens {
  if (hasAccessibleBrandingColors(tokens)) return tokens;
  return { ...tokens, ...Object.fromEntries(colors.map((key) => [key, defaultBranding[key]])) };
}
